/**
 * useSilentAudioLoop Hook
 * 
 * Plays a tiny, inaudible sound using the Web Audio API to prevent browser
 * tab throttling during recording. This is a common technique to keep tabs active.
 * 
 * How it works:
 * - Creates an oscillator at 20kHz (above human hearing range)
 * - Sets gain to 0.001 (-60dB - effectively silent)
 * - Keeps AudioContext in "running" state
 * - Prevents Chrome/Edge from throttling timer-based APIs
 * 
 * Feature gated by RECORDING_FLAGS.SILENT_AUDIO_LOOP
 * 
 * @see Base Docs/Canvas_Performance_Enhancement_Plan.md - Phase 2
 */

import { useRef, useCallback } from 'react';
import { RECORDING_FLAGS } from '../constants/recordingFlags';

export interface UseSilentAudioLoopReturn {
  start: () => void;
  stop: () => void;
  isActive: () => boolean;
}

export function useSilentAudioLoop(): UseSilentAudioLoopReturn {
  const audioContextRef = useRef<AudioContext | null>(null);
  const oscillatorRef = useRef<OscillatorNode | null>(null);
  const gainNodeRef = useRef<GainNode | null>(null);
  const mediaStreamDestRef = useRef<MediaStreamAudioDestinationNode | null>(null);
  const isActiveRef = useRef(false);

  const start = useCallback(() => {
    if (!RECORDING_FLAGS.SILENT_AUDIO_LOOP) return;
    if (isActiveRef.current) return;

    try {
      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioContextClass) {
        return;
      }
      
      const audioContext = new AudioContextClass();
      audioContextRef.current = audioContext;

      const oscillator = audioContext.createOscillator();
      oscillator.frequency.value = 20000;
      oscillator.type = 'sine';

      const gainNode = audioContext.createGain();
      gainNode.gain.value = 0.001;

      // CRITICAL: Route to MediaStreamDestination instead of audioContext.destination
      // This prevents the 20kHz tone from being captured if user is recording system audio
      // while still keeping the AudioContext in "running" state to prevent throttling
      const mediaStreamDest = audioContext.createMediaStreamDestination();
      oscillator.connect(gainNode);
      gainNode.connect(mediaStreamDest);

      oscillator.start();
      oscillatorRef.current = oscillator;
      gainNodeRef.current = gainNode;
      mediaStreamDestRef.current = mediaStreamDest;
      isActiveRef.current = true;

      if (RECORDING_FLAGS.TELEMETRY_ENABLED) {
        console.log('[SilentAudioLoop] Started - preventing tab throttling');
      }
    } catch (error) {
      console.error('[SilentAudioLoop] Failed to start:', error);
    }
  }, []);

  const stop = useCallback(() => {
    if (!isActiveRef.current) return;

    try {
      if (oscillatorRef.current) {
        try {
          oscillatorRef.current.stop();
        } catch {
          // May already be stopped
        }
        oscillatorRef.current.disconnect();
        oscillatorRef.current = null;
      }
      
      if (gainNodeRef.current) {
        gainNodeRef.current.disconnect();
        gainNodeRef.current = null;
      }
      
      if (mediaStreamDestRef.current) {
        mediaStreamDestRef.current.disconnect();
        mediaStreamDestRef.current = null;
      }
      
      if (audioContextRef.current) {
        audioContextRef.current.close().catch(() => {
          // Silently ignore close errors
        });
        audioContextRef.current = null;
      }

      isActiveRef.current = false;

      if (RECORDING_FLAGS.TELEMETRY_ENABLED) {
        console.log('[SilentAudioLoop] Stopped');
      }
    } catch (error) {
      console.error('[SilentAudioLoop] Error stopping:', error);
    }
  }, []);

  const isActive = useCallback(() => {
    return isActiveRef.current;
  }, []);

  return {
    start,
    stop,
    isActive,
  };
}

export default useSilentAudioLoop;
